import React, { useState, useEffect } from 'react';
import { PlaidTransaction, fetchTransactions, getPlaidAccessToken } from '../utils/plaidIntegration';
import { suggestCategory, getSuggestedCategories, CategorySuggestion } from '../utils/categorySuggestion';
import { ExpenseCategory, CATEGORY_LABELS } from '../types/expense';

interface TransactionImportProps {
  onImport: (transactions: ImportedTransaction[]) => void;
  onClose: () => void;
  currentExpenseCount: number;
  isPremium: boolean;
  onUpgradeClick: () => void;
  userId?: string | null;
}

export interface ImportedTransaction {
  transactionId: string;
  date: string;
  amount: number;
  description: string;
  merchantName: string;
  category: ExpenseCategory;
  confidence: number;
  selected: boolean;
}

export default function TransactionImport({ 
  onImport, 
  onClose, 
  currentExpenseCount,
  isPremium,
  onUpgradeClick,
  userId
}: TransactionImportProps) {
  const [transactions, setTransactions] = useState<PlaidTransaction[]>([]);
  const [importedTransactions, setImportedTransactions] = useState<ImportedTransaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<Record<string, ExpenseCategory>>({});
  const [showAlternatives, setShowAlternatives] = useState<Record<string, boolean>>({});
  const [dateRange, setDateRange] = useState({
    startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0]
  });

  const EXPENSE_LIMIT = 5;
  const remainingSlots = isPremium ? Infinity : Math.max(0, EXPENSE_LIMIT - currentExpenseCount);

  // Fetch transactions on mount
  useEffect(() => {
    loadTransactions();
  }, []);

  const loadTransactions = async () => {
    try {
      setLoading(true);
      setError(null);

      const accessToken = getPlaidAccessToken(userId);
      if (!accessToken) {
        throw new Error('Bank not connected');
      }

      const { transactions: txns } = await fetchTransactions(
        accessToken,
        dateRange.startDate,
        dateRange.endDate
      );

      // Filter out income transactions (positive amounts) and pending
      const expenses = txns.filter(t => t.amount < 0 && !t.pending);

      // Sort by date (newest first)
      expenses.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

      setTransactions(expenses);

      // Auto-categorize transactions
      const categorized = expenses.map(t => {
        const suggestion = suggestCategory(t);
        return {
          transactionId: t.transaction_id,
          date: t.date,
          amount: Math.abs(t.amount),
          description: t.name,
          merchantName: t.merchant_name || t.name,
          category: suggestion.category,
          confidence: suggestion.confidence,
          selected: suggestion.confidence >= 70 // Auto-select high confidence
        };
      });

      setImportedTransactions(categorized);

      // Initialize category overrides
      const categories: Record<string, ExpenseCategory> = {};
      categorized.forEach(t => {
        categories[t.transactionId] = t.category;
      });
      setSelectedCategory(categories);

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load transactions. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectAll = (checked: boolean) => {
    setImportedTransactions(prev =>
      prev.map((t, idx) => {
        // Only allow selection if within limit for non-premium users
        if (!isPremium && checked) {
          const currentSelected = prev.slice(0, idx).filter(tx => tx.selected).length;
          return { ...t, selected: currentSelected + currentExpenseCount < EXPENSE_LIMIT };
        }
        return { ...t, selected: checked };
      })
    );
  };

  const handleSelectTransaction = (txnId: string, checked: boolean) => {
    setImportedTransactions(prev => {
      // Allow unchecking always
      if (!checked) {
        return prev.map(t => t.transactionId === txnId ? { ...t, selected: false } : t);
      }
      
      const currentSelected = prev.filter(t => t.selected).length;
      
      // Check if we're at the limit for non-premium users when trying to CHECK a box
      if (!isPremium && currentSelected + currentExpenseCount >= EXPENSE_LIMIT) {
        return prev; // Don't allow selection
      }
      
      return prev.map(t => t.transactionId === txnId ? { ...t, selected: checked } : t);
    });
  };

  const handleCategoryChange = (txnId: string, category: ExpenseCategory) => {
    setSelectedCategory(prev => ({ ...prev, [txnId]: category }));
    setImportedTransactions(prev =>
      prev.map(t => t.transactionId === txnId ? { ...t, category } : t)
    );
  };

  const toggleAlternatives = (txnId: string) => {
    setShowAlternatives(prev => ({ ...prev, [txnId]: !prev[txnId] }));
  };

  const handleImport = () => {
    const selected = importedTransactions.filter(t => t.selected);
    if (selected.length === 0) {
      alert('Please select at least one transaction to import');
      return;
    }
    
    // Final check for non-premium users
    if (!isPremium && selected.length + currentExpenseCount > EXPENSE_LIMIT) {
      alert(`Free users are limited to ${EXPENSE_LIMIT} expenses. You can import ${remainingSlots} more transactions. Upgrade to Premium for unlimited expenses!`);
      return;
    }
    
    onImport(selected);
  };

  const getConfidenceBadge = (confidence: number) => {
    if (confidence >= 80) {
      return <span className="text-xs px-2 py-0.5 bg-green-100 text-green-700 rounded">High</span>;
    } else if (confidence >= 60) {
      return <span className="text-xs px-2 py-0.5 bg-yellow-100 text-yellow-700 rounded">Medium</span>;
    } else {
      return <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-700 rounded">Low</span>;
    }
  };

  const selectedCount = importedTransactions.filter(t => t.selected).length;
  const selectedTotal = importedTransactions
    .filter(t => t.selected)
    .reduce((sum, t) => sum + t.amount, 0);

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-8 max-w-md">
          <div className="flex items-center gap-3">
            <svg className="animate-spin h-8 w-8 text-primary-600" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
            <span className="text-lg">Loading transactions...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg p-6 max-w-md">
          <h3 className="text-xl font-bold text-red-600 mb-4">Error Loading Transactions</h3>
          <p className="text-gray-700 mb-6">{error}</p>
          <div className="flex gap-3">
            <button
              onClick={loadTransactions}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
            >
              Try Again
            </button>
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-white rounded-lg max-w-5xl w-full my-8">
        {/* Header */}
        <div className="border-b border-gray-200 p-6">
          <div className="flex justify-between items-start mb-4">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Import Bank Transactions</h2>
              <p className="text-gray-600 mt-1">
                Review and categorize transactions from your bank account
              </p>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Date range selector */}
          <div className="flex gap-4 items-end">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
              <input
                type="date"
                value={dateRange.startDate}
                onChange={(e) => setDateRange(prev => ({ ...prev, startDate: e.target.value }))}
                className="px-3 py-2 border border-gray-300 rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
              <input
                type="date"
                value={dateRange.endDate}
                onChange={(e) => setDateRange(prev => ({ ...prev, endDate: e.target.value }))}
                className="px-3 py-2 border border-gray-300 rounded-lg"
              />
            </div>
            <button
              onClick={loadTransactions}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
            >
              Refresh
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
          <div className="flex gap-8 items-center">
            <div>
              <div className="text-sm text-gray-600">Total Transactions</div>
              <div className="text-2xl font-bold text-gray-900">{importedTransactions.length}</div>
            </div>
            <div>
              <div className="text-sm text-gray-600">Selected</div>
              <div className="text-2xl font-bold text-primary-600">{selectedCount}</div>
            </div>
            <div>
              <div className="text-sm text-gray-600">Total Amount</div>
              <div className="text-2xl font-bold text-green-600">${selectedTotal.toFixed(2)}</div>
            </div>
            {!isPremium && (
              <div className="ml-auto">
                <div className="bg-yellow-50 border-2 border-yellow-200 rounded-lg px-4 py-2">
                  <div className="text-sm font-semibold text-yellow-800">
                    Free Limit: {currentExpenseCount + selectedCount} / {EXPENSE_LIMIT}
                  </div>
                  <div className="text-xs text-yellow-700 mt-1">
                    {remainingSlots > 0 ? `${remainingSlots} slots remaining` : 'Limit reached'}
                  </div>
                </div>
              </div>
            )}
          </div>
          {!isPremium && remainingSlots === 0 && (
            <div className="mt-3 bg-gradient-to-r from-yellow-50 to-orange-50 border-2 border-yellow-300 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-bold text-gray-900">🔒 Expense Limit Reached</div>
                  <div className="text-sm text-gray-700">Upgrade to Premium for unlimited expenses and imports</div>
                </div>
                <button
                  onClick={onUpgradeClick}
                  className="px-6 py-2 bg-gradient-to-r from-yellow-500 to-orange-500 text-white rounded-lg font-bold hover:shadow-lg transition-shadow whitespace-nowrap"
                >
                  Upgrade - $29
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Transaction list */}
        <div className="p-6 max-h-96 overflow-y-auto">
          {importedTransactions.length === 0 ? (
            <div className="text-center py-12">
              <svg className="w-16 h-16 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <p className="text-gray-600">No transactions found in this date range</p>
            </div>
          ) : (
            <>
              {/* Select all */}
              <div className="flex items-center gap-2 mb-4 pb-2 border-b border-gray-200">
                <input
                  type="checkbox"
                  checked={selectedCount === importedTransactions.length}
                  onChange={(e) => handleSelectAll(e.target.checked)}
                  className="w-4 h-4 cursor-pointer"
                />
                <span className="text-sm font-medium text-gray-700">Select All</span>
              </div>

              {/* Transaction items */}
              <div className="space-y-3">
                {importedTransactions.map((txn) => {
                  const plaidTxn = transactions.find(t => t.transaction_id === txn.transactionId);
                  const alternatives = plaidTxn ? getSuggestedCategories(plaidTxn) : [];

                  return (
                    <div key={txn.transactionId} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-start gap-3">
                        <input
                          type="checkbox"
                          checked={txn.selected}
                          onChange={(e) => handleSelectTransaction(txn.transactionId, e.target.checked)}
                          className="w-5 h-5 mt-1 cursor-pointer flex-shrink-0"
                          disabled={!isPremium && !txn.selected && selectedCount + currentExpenseCount >= EXPENSE_LIMIT}
                        />
                        <div className="flex-1">
                          <div className="flex justify-between items-start mb-2">
                            <div>
                              <div className="font-semibold text-gray-900">{txn.merchantName}</div>
                              <div className="text-sm text-gray-600">{txn.description}</div>
                              <div className="text-xs text-gray-500 mt-1">{txn.date}</div>
                            </div>
                            <div className="text-right">
                              <div className="text-lg font-bold text-gray-900">${txn.amount.toFixed(2)}</div>
                              {getConfidenceBadge(txn.confidence)}
                            </div>
                          </div>

                          {/* Category selector */}
                          <div className="flex gap-2 items-center">
                            <select
                              value={selectedCategory[txn.transactionId]}
                              onChange={(e) => handleCategoryChange(txn.transactionId, e.target.value as ExpenseCategory)}
                              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                            >
                              {Object.entries(CATEGORY_LABELS).map(([value, label]) => (
                                <option key={value} value={value}>{label}</option>
                              ))}
                            </select>
                            {alternatives.length > 1 && (
                              <button
                                onClick={() => toggleAlternatives(txn.transactionId)}
                                className="px-3 py-2 text-sm text-primary-600 hover:text-primary-700"
                              >
                                {showAlternatives[txn.transactionId] ? 'Hide' : 'Suggestions'}
                              </button>
                            )}
                          </div>

                          {/* Alternative suggestions */}
                          {showAlternatives[txn.transactionId] && alternatives.length > 1 && (
                            <div className="mt-2 p-3 bg-gray-50 rounded-lg">
                              <div className="text-xs font-medium text-gray-700 mb-2">Suggested Categories:</div>
                              <div className="space-y-1">
                                {alternatives.slice(1).map((alt, idx) => (
                                  <button
                                    key={idx}
                                    onClick={() => handleCategoryChange(txn.transactionId, alt.category)}
                                    className="block w-full text-left px-3 py-2 text-sm bg-white border border-gray-200 rounded hover:bg-gray-50"
                                  >
                                    <span className="font-medium">{CATEGORY_LABELS[alt.category]}</span>
                                    <span className="text-gray-500 text-xs ml-2">({alt.confidence}% - {alt.reason})</span>
                                  </button>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 p-6 bg-gray-50 flex justify-between items-center">
          <button
            onClick={onClose}
            className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg font-semibold hover:bg-gray-300"
          >
            Cancel
          </button>
          <button
            onClick={handleImport}
            disabled={selectedCount === 0}
            className="px-6 py-3 bg-primary-600 text-white rounded-lg font-semibold hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Import {selectedCount} Transaction{selectedCount !== 1 ? 's' : ''} (${selectedTotal.toFixed(2)})
          </button>
        </div>
      </div>
    </div>
  );
}
